import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { ticketsService } from '../services/tickets';

interface Ticket {
  id: string;
  plate: string;
  parkingId: string;
  status: 'ACTIVE' | 'EXPIRED' | 'CANCELED';
  startTime: string;
  endTime: string;
  amount: number;
  userId: string;
  createdAt: string;
  updatedAt: string;
  user: {
    id: string;
    phone: string;
    email?: string;
  };
  vehicle: {
    id: string;
    plate: string;
    brand?: string;
    model?: string;
    color?: string;
    userId: string;
    createdAt: string;
    updatedAt: string;
  };
  parking: {
    id: string;
    name: string;
    address: string;
    latitude: number;
    longitude: number;
    totalSpots: number;
    availableSpots: number;
    isActive: boolean;
    createdAt: string;
    updatedAt: string;
  };
}

interface TicketsState {
  tickets: Ticket[];
  isLoading: boolean;
  error: string | null;
  total: number;
  currentPage: number;
  pageSize: number;
}

const initialState: TicketsState = {
  tickets: [],
  isLoading: false,
  error: null,
  total: 0,
  currentPage: 1,
  pageSize: 10,
};

export const fetchTickets = createAsyncThunk(
  'tickets/fetch',
  async ({ 
    page = 1, 
    pageSize = 10, 
    status = '', 
    search = '',
    startDate = '',
    endDate = '',
    parkingId = '',
    zoneId = ''
  }: { 
    page?: number; 
    pageSize?: number; 
    status?: string; 
    search?: string;
    startDate?: string;
    endDate?: string;
    parkingId?: string;
    zoneId?: string;
  }) => {
    const response = await ticketsService.getTickets({ 
      page, 
      pageSize, 
      status, 
      search,
      startDate,
      endDate,
      parkingId,
      zoneId
    });
    return (response as any).data;
  }
);

export const updateTicketStatus = createAsyncThunk(
  'tickets/updateStatus',
  async ({ id, status }: { id: string; status: string }) => {
    const response = await ticketsService.updateTicketStatus(id, status);
    return (response as any).data;
  }
);

export const cancelTicket = createAsyncThunk(
  'tickets/cancel',
  async (id: string) => {
    const response = await ticketsService.cancelTicket(id);
    return (response as any).data;
  }
);

const ticketsSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload;
    },
    setPageSize: (state, action) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTickets.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        
        // Log pour debug
        console.log('fetchTickets.fulfilled - action.payload:', action.payload);
        
        // Gérer la structure { data: { tickets, pagination }, success: true }
        if (!action.payload) {
          console.error('fetchTickets.fulfilled: action.payload is undefined');
          state.error = 'Données non disponibles';
          return;
        }
        
        const response = action.payload.data || action.payload;
        if (!response || !response.tickets) {
          console.error('fetchTickets.fulfilled: response structure invalid', response);
          state.error = 'Structure de données invalide';
          return;
        }
        
        console.log('Tickets reçus:', response.tickets.length);
        console.log('Pagination:', response.pagination);
        
        state.tickets = response.tickets;
        state.total = response.pagination?.total || 0;
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.error.message || 'Erreur lors du chargement';
      })
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        const index = state.tickets.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tickets[index] = action.payload;
        }
      })
      .addCase(cancelTicket.fulfilled, (state, action) => {
        const index = state.tickets.findIndex(t => t.id === action.payload.id);
        if (index !== -1) {
          state.tickets[index].status = 'CANCELED';
        }
      });
  },
});

export const { setCurrentPage, setPageSize } = ticketsSlice.actions;
export default ticketsSlice.reducer;
